/**
 * Reports Index Page Component
 *
 * Main entry point for reports - displays list of all available reports
 */

import React from 'react';
import {useNavigate} from 'react-router-dom';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {AppTitle, AppText, AppCardTitle} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';
import Button from '../../../Presentation/components/ui/Button';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';

interface ReportCard {
  id: string;
  title: string;
  description: string;
  icon: string;
  route: string;
  permission?: string;
  category: 'marketer' | 'products' | 'sales' | 'financial';
}

const AVAILABLE_REPORTS: ReportCard[] = [
  {
    id: 'marketer',
    title: 'تقارير المسوقين',
    description: 'عرض تقارير المسوقين المالية والمبيعات والمخزون مع تفاصيل المتاجر',
    icon: '👥',
    route: '/dashboard/reports/marketer',
    permission: PERMISSIONS.DASHBOARD_REPORTS,
    category: 'marketer'
  },
  {
    id: 'products-sales',
    title: 'تقرير مبيعات المنتجات',
    description: 'عرض تفاصيل مبيعات جميع المنتجات مع الإحصائيات والتحليلات',
    icon: '📦',
    route: '/dashboard/reports/products-sales',
    permission: PERMISSIONS.REPORTS_READ,
    category: 'products'
  }
];

const ReportsIndexPage: React.FC = () => {
  const navigate = useNavigate();
  const {hasPermission} = useAuthorization();

  // Filter reports based on permissions
  const accessibleReports = AVAILABLE_REPORTS.filter(report => {
    if (!report.permission) return true;
    return hasPermission(report.permission);
  });

  const handleReportClick = (route: string) => {
    navigate(route);
  };

  return (
    <SidebarLayout>
      <div className="h-full flex flex-col p-6">
        {/* Page Header */}
        <div className="mb-6">
          <AppTitle className="text-3xl font-bold text-gray-900 mb-2">
            التقارير
          </AppTitle>
          <AppText className="text-gray-600">
            اختر التقرير المطلوب لعرض البيانات والتحليلات التفصيلية
          </AppText>
        </div>

        {/* Reports Grid */}
        <div className="flex-1 overflow-y-auto">
          {accessibleReports.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {accessibleReports.map((report) => (
                <Card
                  key={report.id}
                  className="p-6 hover:shadow-lg transition-all duration-200 cursor-pointer border-2 border-transparent hover:border-brand-200"
                  onClick={() => handleReportClick(report.route)}
                >
                  <div className="flex flex-col h-full">
                    {/* Icon */}
                    <div className="text-5xl mb-4 text-center">{report.icon}</div>

                    {/* Title */}
                    <AppCardTitle className="text-xl font-bold text-gray-900 mb-2 text-center">
                      {report.title}
                    </AppCardTitle>

                    {/* Description */}
                    <AppText className="text-gray-600 mb-6 flex-1 text-center">
                      {report.description}
                    </AppText>

                    {/* Action Button */}
                    <Button
                      variant="primary"
                      size="medium"
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReportClick(report.route);
                      }}
                    >
                      عرض التقرير
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <div className="text-gray-400 mb-4">
                <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <AppCardTitle className="text-gray-500 mb-2">
                لا توجد تقارير متاحة
              </AppCardTitle>
              <AppText className="text-gray-400">
                ليس لديك صلاحية للوصول إلى أي تقارير حالياً
              </AppText>
            </Card>
          )}
        </div>
      </div>
    </SidebarLayout>
  );
};

export default ReportsIndexPage;

